/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { Separator } from '@fluentui/react/lib/Separator';
import { getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { SavingState } from 'src/clu/appShell/cluTagUtterancesPage/CluTagUtterancesUiStore';
import { cluTeachingBubbleMessages } from 'src/clu/cluTeachingBubbleMessages';
import { CluUtteranceStore } from 'src/clu/core/stores/CluUtteranceStore';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { DatasetType } from 'src/modules/modelTraining/types/trainingTypes';

export type UtteranceCommandBarProps = TableCommandBarProps<CluUtteranceStore> & {
    onEdit: () => void;
    onSave: () => void;
    onDelete: () => void;
    onFilter: () => void;
    onUpload: () => void;
    utteranceCount: number;
    savingState: SavingState;
    isFilterSelected: boolean;
    currentProjectName: string;
    onMoveToIntent: () => void;
    onMoveToDataset: () => void;
    currentActiveDataset: DatasetType;
    onUtterancesGenerationWithGpt: () => void;
    onAcceptAllGeneratedUtterances: () => void;
    onRejectAllGeneratedUtterances: () => void;
};

const messages = defineMessages({
    edit: { id: 'clu.cluUtteranceCommandBar.edit', defaultMessage: 'Edit' },
    delete: { id: 'clu.cluUtteranceCommandBar.delete', defaultMessage: 'Delete' },
    filter: { id: 'clu.cluUtteranceCommandBar.filter', defaultMessage: 'Filter' },
    moveTo: { id: 'clu.cluUtteranceCommandBar.moveTo', defaultMessage: 'Move to' },
    testing: { id: 'clu.utteranceCommandBar.testing', defaultMessage: 'Testing dataset' },
    acceptAll: { id: 'clu.cluUtteranceCommandBar.acceptAll', defaultMessage: 'Accept all' },
    rejectAll: { id: 'clu.cluUtteranceCommandBar.rejectAll', defaultMessage: 'Reject all' },
    training: { id: 'clu.utteranceCommandBar.training', defaultMessage: 'Training dataset' },
    saveChanges: { id: 'clu.utteranceCommandBar.saveChanges', defaultMessage: 'Save changes' },
    moveToIntent: { id: 'clu.utteranceCommandBar.moveToIntent', defaultMessage: 'Different intent' },
    searchPlaceholder: { id: 'clu.cluUtteranceCommandBar.searchPlaceholder', defaultMessage: 'Search' },
    suggestUtterances: { id: 'clu.cluUtteranceCommandBar.suggestUtterances', defaultMessage: 'Suggest utterances' },
    uploadUtteranceFile: { id: 'clu.cluUtteranceCommandBar.uploadUtteranceFile', defaultMessage: 'Upload utterance file' }
});

export const CluUtteranceCommandBar = observer((props: UtteranceCommandBarProps) => {
    const {
        query,
        onEdit,
        onSave,
        onUpload,
        onDelete,
        onFilter,
        savingState,
        selectedItems,
        onQueryChange,
        onMoveToIntent,
        utteranceCount,
        onMoveToDataset,
        isFilterSelected,
        currentActiveDataset,
        onUtterancesGenerationWithGpt,
        onAcceptAllGeneratedUtterances,
        onRejectAllGeneratedUtterances
    } = props;

    const { localize } = useLocalization();
    const { uiStore, rootStore } = useGlobalState();

    const moveButtonIdRef = React.useRef<string>(getId());
    const editButtonIdRef = React.useRef<string>(getId());
    const saveButtonIdRef = React.useRef<string>(getId());
    const deleteButtonIdRef = React.useRef<string>(getId());
    const filterItemIdRef = React.useRef<string>(getId());

    const [isMoveButtonDisabled, setIsMoveButtonDisabled] = React.useState(true);
    const [isEditButtonDisabled, setIsEditButtonDisabled] = React.useState(true);
    const [isSaveButtonDisabled, setIsSaveButtonDisabled] = React.useState(true);
    const [isDeleteButtonDisabled, setIsDeleteButtonDisabled] = React.useState(true);

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    React.useEffect(
        () => () => {
            dequeueTeachingBubble('CluFilterView');
            dequeueTeachingBubble('CluSaveChanges');
            dequeueTeachingBubble('CluEditUtterance');
            dequeueTeachingBubble('CluMoveUtterance');
            dequeueTeachingBubble('CluDeleteUtterance');
        },
        []
    );

    React.useEffect(() => {
        if (
            !isMoveButtonDisabled &&
            uiStore.teachingBubbles.CluAddEntity &&
            !uiStore.teachingBubbles.CluMoveUtterance &&
            !uiStore.skippedTeachingBubbles.CluAddEntity
        ) {
            createTeachingBubble(
                'CluMoveUtterance',
                localize(cluTeachingBubbleMessages.moveUtteranceBody),
                localize(cluTeachingBubbleMessages.moveUtteranceTitle),
                `#${moveButtonIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            !isEditButtonDisabled &&
            uiStore.teachingBubbles.CluMoveUtterance &&
            !uiStore.teachingBubbles.CluEditUtterance &&
            !uiStore.skippedTeachingBubbles.CluMoveUtterance
        ) {
            createTeachingBubble(
                'CluEditUtterance',
                localize(cluTeachingBubbleMessages.editUtteranceBody),
                localize(cluTeachingBubbleMessages.editUtteranceTitle),
                `#${editButtonIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            !isDeleteButtonDisabled &&
            uiStore.teachingBubbles.CluEditUtterance &&
            !uiStore.teachingBubbles.CluDeleteUtterance &&
            !uiStore.skippedTeachingBubbles.CluEditUtterance
        ) {
            createTeachingBubble(
                'CluDeleteUtterance',
                localize(cluTeachingBubbleMessages.deleteUtteranceBody),
                localize(cluTeachingBubbleMessages.deleteUtteranceTitle),
                `#${deleteButtonIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            !isSaveButtonDisabled &&
            !uiStore.teachingBubbles.CluSaveChanges &&
            uiStore.teachingBubbles.CluFilterView &&
            !uiStore.skippedTeachingBubbles.CluFilterView
        ) {
            createTeachingBubble(
                'CluSaveChanges',
                localize(cluTeachingBubbleMessages.saveChangesBody),
                localize(cluTeachingBubbleMessages.saveChangesTitle),
                `#${saveButtonIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            utteranceCount &&
            !uiStore.teachingBubbles.CluFilterView &&
            uiStore.teachingBubbles.CluSelectIntent &&
            !uiStore.skippedTeachingBubbles.CluSelectIntent
        ) {
            createTeachingBubble(
                'CluFilterView',
                localize(cluTeachingBubbleMessages.filterViewBody),
                localize(cluTeachingBubbleMessages.filterViewTitle),
                `#${filterItemIdRef.current}`,
                true,
                true
            );
        }
    }, [
        utteranceCount,
        isMoveButtonDisabled,
        isEditButtonDisabled,
        isSaveButtonDisabled,
        isDeleteButtonDisabled,
        Object.values(uiStore.teachingBubbles)
    ]);

    const commandBarItems: ICommandBarItemProps[] = [
        {
            key: 'save',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled = item.disabled || savingState !== 'dirty';
                setIsSaveButtonDisabled(!canWrite || _disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="saveUtteranceButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                onClick={onSave}
                                id={saveButtonIdRef.current}
                                iconProps={{ iconName: 'Save' }}
                                disabled={!canWrite || _disabled}
                                text={localize(messages.saveChanges)}
                                name={localize(messages.saveChanges)}
                                data-automation-id="saveUtteranceButton"
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                );
            }
        },
        { key: 'driverItem', onRender: () => <Separator vertical styles={{ root: { padding: 0 } }} /> },
        {
            key: 'upload',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="uploadUtteranceButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={onUpload}
                            iconProps={{ iconName: 'Upload' }}
                            disabled={!canWrite || item.disabled}
                            data-automation-id="uploadUtteranceButton"
                            text={localize(messages.uploadUtteranceFile)}
                            name={localize(messages.uploadUtteranceFile)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        FeatureToggles.CluGptGeneration && {
            key: 'generateUtterancesWithGpt',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="generateGptUtteranceButtonToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            disabled={!canWrite || item.disabled}
                            onClick={onUtterancesGenerationWithGpt}
                            iconProps={{ iconName: 'FullChatSparkle' }}
                            text={localize(messages.suggestUtterances)}
                            name={localize(messages.suggestUtterances)}
                            data-automation-id="generateGptUtteranceButton"
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'moveTo',
            onRender: (item: ICommandBarItemProps) => {
                setIsMoveButtonDisabled(!canWrite || item.disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || item.disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="moveToButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                id={moveButtonIdRef.current}
                                text={localize(messages.moveTo)}
                                name={localize(messages.moveTo)}
                                data-automation-id="moveToButton"
                                disabled={!canWrite || item.disabled}
                                iconProps={{ iconName: 'MoveToFolder' }}
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                                menuProps={{
                                    items: [
                                        {
                                            key: 'intents',
                                            onClick: onMoveToIntent,
                                            text: localize(messages.moveToIntent),
                                            iconProps: { iconName: 'MoveToFolder' }
                                        },
                                        {
                                            key: 'datasets',
                                            onClick: onMoveToDataset,
                                            iconProps: { iconName: 'Database' },
                                            text: localize(currentActiveDataset === 'Train' ? messages.testing : messages.training)
                                        }
                                    ]
                                }}
                            />
                        }
                    />
                );
            }
        },
        {
            key: 'edit',
            onRender: (item: ICommandBarItemProps) => {
                setIsEditButtonDisabled(!canWrite || item.disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || item.disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="editUtteranceButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                onClick={onEdit}
                                id={editButtonIdRef.current}
                                text={localize(messages.edit)}
                                name={localize(messages.edit)}
                                iconProps={{ iconName: 'Rename' }}
                                disabled={!canWrite || item.disabled}
                                data-automation-id="editUtteranceButton"
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                );
            }
        }
    ].filter(Boolean);

    const overflowCommandBarItems: ICommandBarItemProps[] = [
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => {
                setIsDeleteButtonDisabled(!canWrite || !selectedItems.length);

                return (
                    <RBACTooltip
                        hidden={canWrite || !selectedItems.length}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="deleteUtteranceButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                onClick={onDelete}
                                id={deleteButtonIdRef.current}
                                text={localize(messages.delete)}
                                name={localize(messages.delete)}
                                iconProps={{ iconName: 'Delete' }}
                                data-automation-id="deleteUtteranceButton"
                                disabled={!canWrite || !selectedItems.length}
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                );
            }
        },
        FeatureToggles.CluGptGeneration && {
            key: 'acceptAllGeneratedUtterance',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="acceptAllGeneratedGptUtterancesButtonToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            text={localize(messages.acceptAll)}
                            name={localize(messages.acceptAll)}
                            disabled={!canWrite || item.disabled}
                            iconProps={{ iconName: 'CheckList' }}
                            onClick={onAcceptAllGeneratedUtterances}
                            data-automation-id="acceptAllGeneratedGptUtterancesButton"
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        FeatureToggles.CluGptGeneration && {
            key: 'rejectAllGeneratedUtterance',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="rejectAllGeneratedGptUtterancesButtonToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            text={localize(messages.rejectAll)}
                            name={localize(messages.rejectAll)}
                            disabled={!canWrite || item.disabled}
                            onClick={onRejectAllGeneratedUtterances}
                            iconProps={{ iconName: 'RemoveFromShoppingList' }}
                            data-automation-id="rejectAllGeneratedGptUtterancesButton"
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        }
    ];

    const items = commandBarItemSelectionManager(
        commandBarItems,
        ['upload', 'save', 'generateUtterancesWithGpt', 'acceptAllGeneratedUtterance', 'rejectAllGeneratedGptUtterancesButtonToolTip'],
        ['moveTo', 'edit', 'delete', 'save'],
        ['moveTo', 'delete', 'save'],
        selectedItems.length
    );

    const commandBarFarItems: ICommandBarItemProps[] = [
        {
            key: 'filter',
            id: filterItemIdRef.current,
            onClick: onFilter,
            text: localize(messages.filter),
            iconProps: { iconName: isFilterSelected ? 'FilterSolid' : 'Filter' }
        }
    ];

    const shouldOverFlowItems = () => !uiStore.isSidebarCollapsed && query.length;

    return (
        <LuisCommandBar
            query={query}
            showTopBorder
            includeSearchBar
            onQueryChange={onQueryChange}
            farItems={commandBarFarItems}
            overflowItems={shouldOverFlowItems() ? overflowCommandBarItems : []}
            items={shouldOverFlowItems() ? items : [...items, ...overflowCommandBarItems]}
        />
    );
});
